import { useState } from 'react';
import { Storage } from '../lib/storage';
import { errMsg } from '../lib/errors';
import { hasSavedBookingHours } from '../lib/bookingHours';
import { BOOKING_DEFAULT_CONFIG } from '../engine';

// ─────────────────────────────────────────────────────────────────────
// useBookingSettings — the booking-module ADMIN settings (price zones +
// config), lifted out of the Padel24AppMain god component (see
// docs/history/DECOMPOSITION_PLAN.md, PR-C).
//
// These two slices are the lowest-coupling persisted state after clubInfo:
//   • bookingZones  — price zones (Storage key 'booking_zones_v1')
//   • bookingConfig — buffer rule, opening hours, durations, enabled courts,
//                     court metadata, … (Storage key 'booking_config_v1')
// Each has a SINGLE Storage-first mutator, NO blanket-persist effect, and NO
// §13.20 loaded-flag (so there is no "write before load" race). They are read
// by the booking engine (validation / slot generation) but written only here.
//
// Unlike useClubInfo (which owns its own load effect), the INITIAL load of
// these slices stays in the monolith's ATOMIC mount loader — it reads them
// alongside ~13 other domains in one pass and flushes them via setBookingZones
// / setBookingConfig (exposed below). Keeping the cross-slice loader atomic is
// a deliberate constraint (DECOMPOSITION_PLAN principle #1 / R1); when the
// loader later moves into StoreProvider it will call these same setters.
//
// The config default comes from the shared engine ('../engine')
// (Padel24Engine.booking.DEFAULT_CONFIG IS BOOKING_DEFAULT_CONFIG). The zones
// start EMPTY: there is no default zone (#1312), and a club with no saved zone
// has no court price — the screens say «Pris ikke satt».
// ─────────────────────────────────────────────────────────────────────

/** A booking price zone (loosely typed — the engine treats zones as `any`). */
export type BookingZone = any;
/** Booking-module configuration (loosely typed — the engine treats it as `any`). */
export type BookingConfig = any;

/** Result of a settings save — mirrors the monolith's return shape exactly. */
export interface BookingSettingsUpdateResult {
  success: boolean;
  error?: string;
}

export function useBookingSettings() {
  // No zones until the club saves some (#1312, owner decision 2026-11-03) —
  // never an invented one, whose price the server would not charge.
  const [bookingZones, setBookingZones] = useState<BookingZone[]>(() => []);
  // Default config (buffer rule, opening hours, etc.) merged on load.
  const [bookingConfig, setBookingConfig] = useState<BookingConfig>(() => ({
    ...BOOKING_DEFAULT_CONFIG,
  }));
  // Whether the STORED config carries opening hours — no booking opens before
  // it does (owner decision 2026-10-31, #1192). Null until the loader has
  // answered, so nothing is claimed about a document nobody has read yet.
  const [bookingHoursSaved, setBookingHoursSaved] = useState<boolean | null>(null);

  // Price-zone CRUD — admin edits the whole list at once from the editor modal.
  // Storage-first (HANDOVER §13): await Storage.set BEFORE setState.
  const updateBookingZones = async (
    newZones: BookingZone[],
  ): Promise<BookingSettingsUpdateResult> => {
    try {
      await Storage.set('booking_zones_v1', JSON.stringify(newZones));
      setBookingZones(newZones);
      return { success: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre pris-soner:', e);
      return { success: false, error: errMsg(e) };
    }
  };

  // Booking config (buffer rule, opening hours, durations, …). Storage-first.
  const updateBookingConfig = async (
    newConfig: BookingConfig,
  ): Promise<BookingSettingsUpdateResult> => {
    try {
      await Storage.set('booking_config_v1', JSON.stringify(newConfig));
      setBookingConfig(newConfig);
      // What was just stored is the document now.
      setBookingHoursSaved(hasSavedBookingHours(newConfig));
      return { success: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre booking-konfig:', e);
      return { success: false, error: errMsg(e) };
    }
  };

  return {
    bookingZones,
    bookingConfig,
    // Exposed so the monolith's atomic mount loader can flush the loaded values
    // (these go away once the loader moves into StoreProvider).
    setBookingZones,
    setBookingConfig,
    bookingHoursSaved,
    setBookingHoursSaved,
    updateBookingZones,
    updateBookingConfig,
  };
}
